import { autobind } from 'core-decorators'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { History, match } from 'react-router'
import { OfficeInstance } from '../../../models/Office'
import { InnerContainer } from '../../../shared/components/Elements'
import { Loading } from '../../../shared/components/Loading'
import { breakpoint, styled } from '../../../shared/styled-components'
import { CalculatorStoreInstance } from '../../../stores/CalculatorStore'
import { CalculatorOptions } from '../../calculator'
import { CodeBadge } from './result/CodeBadge'
import { InsuranceSuggestions } from './result/insurances/InsuranceSuggestions'
import { ResultHeader } from './result/ResultHeader'
import { QuestionForm } from './result/QuestionForm'
import { Benefits } from './result/Benefits'
import { Treatment } from './result/Treatment'
import { OfficeListBox } from './result/OfficeListBox'
import { Costs } from './result/Costs'
import { SendReportOverlay } from './result/SendReportOverlay'
import { VideoOverlay } from './result/VideoOverlay'

interface IProps {
  store?: CalculatorStoreInstance
  match?: match<{ code: string }>
  options?: CalculatorOptions
  history?: History
}

interface IState {
  loading: boolean
  loadingOffices: boolean
  offices: OfficeInstance[]
  error: boolean
  canLoadMore: boolean
  sendReportOverlayVisible: boolean
  videoOffice?: OfficeInstance
  videoOverlayVisible: boolean
}

@inject('store', 'options')
@observer
export class ResultView extends React.Component<IProps, IState> {
  private static OFFICES_PER_PAGE = 3
  state: IState = {
    loading: true,
    loadingOffices: false,
    offices: [],
    canLoadMore: true,
    error: false,
    sendReportOverlayVisible: false,
  }

  private async _fetchOffer() {
    const { store, match, options } = this.props
    await store!.fetchOffer(match.params.code)
    if (!options.apiKey) {
      await store!.fetchOffices(store!.offer.code, ResultView.OFFICES_PER_PAGE, {
        exclude_ids: store.offices.map((o) => o.id),
      })
    }
    this.setState({ loading: false })
  }

  @autobind
  private async _loadMoreOffices() {
    this.setState({ loadingOffices: true })
    const { store, match } = this.props
    const newOffices = await store!.fetchOffices(match.params.code, ResultView.OFFICES_PER_PAGE, {
      exclude_ids: store.offices.map((o) => o.id),
    })
    this.setState({ loadingOffices: false, canLoadMore: newOffices.length >= ResultView.OFFICES_PER_PAGE })
  }

  // -------------------------------------------------------------------------- LIFECYCLE

  async componentDidMount() {
    const { store, match, history } = this.props

    if (match.params.code) {
      await this._fetchOffer()
    } else if (store!.validateRequirementsGiven()) {
      try {
        await store!.calculateOffer()
        history.replace('/implantatkostenrechner/ergebnis/' + store!.offer.code)
      } catch (e) {
        this.setState({ loading: false, error: true })
      }
    }
  }

  async componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
    if (prevProps.match.params.code !== this.props.match.params.code) {
      await this._fetchOffer()
    }
  }

  // -------------------------------------------------------------------------- RENDERING

  private _renderError() {
    return (
      <Error>
        Bei der Berechnung Ihres Angebotes ist leider ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
      </Error>
    )
  }

  @autobind
  private _openSendReportOverlay() {
    this.setState({ sendReportOverlayVisible: true })
  }

  @autobind
  private _closeSendReportOverlay() {
    this.setState({ sendReportOverlayVisible: false })
  }

  @autobind
  private _openVideo(office: OfficeInstance) {
    this.setState({ videoOffice: office, videoOverlayVisible: true })
  }

  @autobind
  private _closeVideoOverlay() {
    this.setState({ videoOverlayVisible: false })
  }

  private _renderReport() {
    const { options, store } = this.props
    const { offer, offices } = store
    const { loadingOffices, sendReportOverlayVisible, videoOverlayVisible, videoOffice } = this.state

    return (
      <Container>
        <ResultHeader onSendReport={this._openSendReportOverlay} />
        <Inner>
          <Top>
            <CostsContainer>
              <Costs {...{ offer }} />
            </CostsContainer>
            <Treatment {...{ offer }} />
            <DesktopCodeBadgeContainer>
              <CodeBadge {...{ offer }} />
            </DesktopCodeBadgeContainer>
          </Top>
          <Bottom>
            <BottomLeft>
              <QuestionForm office={offices[0]} />
              <Benefits />
            </BottomLeft>
            {!options.apiKey && (
              <OfficeListBox
                loading={loadingOffices}
                offices={offices}
                onLoadMore={this._loadMoreOffices}
                onVideoOpen={this._openVideo}
              />
            )}
          </Bottom>
        </Inner>
        <InsuranceSuggestions selfExpense={offer.selfExpense} />
        {sendReportOverlayVisible && <SendReportOverlay onClose={this._closeSendReportOverlay} {...{ offer }} />}
        {videoOverlayVisible && <VideoOverlay onClose={this._closeVideoOverlay} office={videoOffice} />}
      </Container>
    )
  }

  render(): React.ReactNode {
    const { loading, error } = this.state

    return <div>{loading ? <Loading /> : error ? this._renderError() : this._renderReport()}</div>
  }
}

const Container = styled.div`
  color: ${(props) => props.theme.blueColor};
`

const Error = styled.div`
  font-weight: bold;
  text-align: center;
  color: ${(props) => props.theme.primaryColor};
  font-size: 14px;
`

const Inner = styled(InnerContainer)`
  display: flex;
  flex-direction: column;
`

const Top = styled.div`
  display: flex;
  flex-direction: column;
  ${breakpoint('tablet')`
    flex-direction: row;
  `};
`

const CostsContainer = styled.div`
  flex: 0 0 30%;
`

const Bottom = styled.div`
  display: flex;
  flex-direction: column-reverse;
  flex: 1;
  ${breakpoint('tablet')`
    flex-direction: row;
  `};
`

const BottomLeft = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 30%;
`

const DesktopCodeBadgeContainer = styled.div`
  display: none;
  ${breakpoint('tablet')`
    display: block;
  `};
`
